/**
 * ProfilePage - Role-based profile router
 * Shows appropriate profile page based on user role
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Container, Alert, CircularProgress, Box, Button } from '@mui/material';
import ClientProfile from './ClientProfile';
import VerifierProfile from './VerifierProfile_Enhanced';
import ContractorProfilePage from './ContractorProfilePage';
import ProjectManagerProfile from './ProjectManagerProfile';

const ProfilePage = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Not authenticated
  if (!isAuthenticated || !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          Please log in to view your profile.
        </Alert>
      </Container>
    );
  }

  // Route to appropriate profile based on role
  const userRole = user.role?.toLowerCase();

  switch (userRole) {
    case 'client':
      return <ClientProfile />;
    
    case 'verifier':
    case 'professional_verifier':
    case 'quality_inspector':
      return <VerifierProfile />;
    
    case 'contractor':
      return <ContractorProfilePage />;
    
    case 'project_manager':
      return <ProjectManagerProfile />;
    
    case 'realtor':
      return <Navigate to="/realtor/profile" replace />;
    case 'conveyancer':
      return <Navigate to="/conveyancer/profile" replace />;

    case 'merchant':
      // Redirect to merchant profile management
      return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Merchant profile management is available in your dashboard.
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.location.href = '/dashboard'}
              sx={{ bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' } }}
            >
              Go to Merchant Dashboard
            </Button>
          </Box>
        </Container>
      );
    
    case 'admin':
    case 'moderator':
      // Admins can view all profiles, default to client profile view
      return <ClientProfile />;
    
    default:
      return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="info">
            Profile view not available for your role: {user.role}
          </Alert>
        </Container>
      );
  }
};

export default ProfilePage;
